import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import YearSelector from '../../../Common/Selectors/YearSelector';
import PeriodSelector from '../../../Common/Selectors/PeriodSelector';
import { creators as WorkGeneratedActions } from '../../../Reducers/Reporting/WorkGenerated';
import { creators as ReportSettingActions } from '../../../Reducers/Reporting/ReportSetting';
import { financialMonth } from '../../../Utils/Functions';
import appdata from '../../../Services/appdata';
import TopChart from './TopChart';
import MiddleChart from './MiddleChart';
import RightChart from './RightChart';
import BottomChart from './BottomChart';

const WorkGenerated = (props) => {
  const {
    selectedCompany,
    defaultYear,
    defaultMonth,
    defaultStartMonth,
    defaultDimDate,
    workGeneratedSetting,
    bubbleFlag,
  } = props;

  // Redux hooks
  const dispatch = useDispatch();
  const workGeneratedSummary = useSelector(state => state.Reporting.Summary.reportingSummaries?.workGenerated ?? '');
  const queryData = useSelector((state) => state.Reporting.WorkGenerated.queryData);
  const lastFetchedCompany = useSelector(
    (state) => state?.Reporting?.Default?.defaultCompany ?? null,
  );
  const lastFetchedDimDate = useSelector(
    (state) => state?.Reporting?.Default?.defaultDimDate ?? null,
  );

  // Local state
  const [mChartHeight, setMChartHeight] = useState(200);
  const [selectedYears, setSelectedYears] = useState([]);
  const [label, setLabel] = useState('');
  const [period, setPeriod] = useState('');
  const [filterName, setFilterName] = useState('');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedTopItems, setSelectedTopItems] = useState([]);
  const [selectedMiddleItems, setSelectedMiddleItems] = useState([]);
  const [selectedRightItems, setSelectedRightItems] = useState([]);
  const [isMounted, setIsMounted] = useState(false);

  // Redux action creators
  const getQuery = useCallback((selectedYears) => {
    dispatch(WorkGeneratedActions.workgeneratedQueryRequest(selectedYears));
  }, [dispatch]);

  const reportSettingUpdateRequest = useCallback((update_setting) => {
    dispatch(ReportSettingActions.reportSettingUpdateRequest(update_setting));
  }, [dispatch]);

  const onResize = useCallback(() => {
    if (isMounted) {
      // Trigger re-render for resize
      setMChartHeight(prev => prev);
    }
  }, [isMounted]);

  // Component mount effect
  useEffect(() => {
    setIsMounted(true);
    window.addEventListener('resize', onResize);

    setSelectedYears([defaultYear]);
    setLabel(defaultYear.toString());
    setSelectedMonths([financialMonth(defaultMonth, defaultStartMonth) - 1]);
    setSelectedTopItems(
      appdata.getLocalItem('wgSelectedTopItems')
        ? appdata.getLocalItem('wgSelectedTopItems')
        : []
    );
    setSelectedMiddleItems(
      appdata.getLocalItem('wgSelectedMiddleItems')
        ? appdata.getLocalItem('wgSelectedMiddleItems')
        : []
    );
    setSelectedRightItems(
      appdata.getLocalItem('wgSelectedRightItems')
        ? appdata.getLocalItem('wgSelectedRightItems')
        : []
    );

    if (workGeneratedSetting) {
      const setting = JSON.parse(workGeneratedSetting);
      setPeriod(setting.period);
      setFilterName(setting.filterName);
    } else {
      reportSettingUpdateRequest({
        WorkGenerated: JSON.stringify({
          period: 'month',
          filterName: '',
        }),
      });
    }

    // Cleanup
    return () => {
      setIsMounted(false);
      window.removeEventListener('resize', onResize);
    };
  }, []); // Empty dependency array for mount effect

  // Effect for props changes
  useEffect(() => {
    if (isMounted) {
      setSelectedYears([defaultYear]);
      setLabel(defaultYear.toString());
      setSelectedMonths([financialMonth(defaultMonth, defaultStartMonth) - 1]);
      setSelectedTopItems([]);
      setSelectedMiddleItems([]);
      setSelectedRightItems([]);

      appdata.setLocalItem('wgSelectedTopItems', []);
      appdata.setLocalItem('wgSelectedMiddleItems', []);
      appdata.setLocalItem('wgSelectedRightItems', []);

      if (!workGeneratedSetting) {
        reportSettingUpdateRequest({
          WorkGenerated: JSON.stringify({
            period: 'month',
            filterName: '',
          }),
        });
      }
    }
  }, [defaultYear, defaultMonth, defaultStartMonth, isMounted, workGeneratedSetting, reportSettingUpdateRequest]);

  const triggerWorkGeneratedRequests = useCallback(() => {
    getQuery([defaultYear]);
  }, [defaultYear, getQuery]);

  useEffect(() => {
    if (!isMounted) return;

    if (!lastFetchedCompany || !lastFetchedDimDate) {
      triggerWorkGeneratedRequests();
    }
  }, [defaultYear, lastFetchedCompany, lastFetchedDimDate, isMounted, triggerWorkGeneratedRequests]);

  useEffect(() => {
    if (!isMounted) return;

    const valuesChanged =
      lastFetchedCompany !== selectedCompany ||
      lastFetchedDimDate !== defaultDimDate;

    if (
      valuesChanged &&
      lastFetchedCompany &&
      lastFetchedDimDate
    ) {
      triggerWorkGeneratedRequests();
    }
  }, [defaultYear, defaultDimDate, selectedCompany, lastFetchedCompany, lastFetchedDimDate, isMounted, triggerWorkGeneratedRequests]);

  const handleYear = useCallback(async (event) => {
    if (event.selectedYears.length === 0) {
      toast.warning('Please select the year.');
    }

    setSelectedMonths([]);
    setSelectedTopItems([]);
    appdata.setLocalItem('wgSelectedTopItems', []);

    await getQuery(event.selectedYears);
    handleFilter(event);
  }, [getQuery]);

  const handleFilter = useCallback(async (event) => {
    // Update state based on event
    if (event.selectedYears !== undefined) setSelectedYears(event.selectedYears);
    if (event.label !== undefined) setLabel(event.label);
    if (event.period !== undefined) setPeriod(event.period);
    if (event.filterName !== undefined) setFilterName(event.filterName);
    if (event.selectedMonths !== undefined) setSelectedMonths(event.selectedMonths);
    if (event.selectedTopItems !== undefined) setSelectedTopItems(event.selectedTopItems);
    if (event.selectedMiddleItems !== undefined) setSelectedMiddleItems(event.selectedMiddleItems);
    if (event.selectedRightItems !== undefined) setSelectedRightItems(event.selectedRightItems);
    if (event.mChartHeight !== undefined) setMChartHeight(event.mChartHeight);

    // Save to local storage
    if (event.selectedTopItems) {
      appdata.setLocalItem('wgSelectedTopItems', event.selectedTopItems);
    }
    if (event.selectedMiddleItems) {
      appdata.setLocalItem('wgSelectedMiddleItems', event.selectedMiddleItems);
    }
    if (event.selectedRightItems) {
      appdata.setLocalItem('wgSelectedRightItems', event.selectedRightItems);
    }

    // Update report settings
    if (event.period || event.filterName) {
      await reportSettingUpdateRequest({
        WorkGenerated: JSON.stringify({
          period: event.period ? event.period : period,
          filterName: event.filterName ? event.filterName : filterName,
        }),
      });
    }
  }, [period, filterName, reportSettingUpdateRequest]);

  return (
    <div style={{ height: '89vh' }}>
      <div id="wg_year_selector" className="wrapper">
        <PeriodSelector period={period} onChange={handleFilter} />
        <div>
          <YearSelector
            selectedYears={selectedYears}
            label={label}
            onChange={handleYear}
          />
        </div>

        <div className="right well"></div>
      </div>

      <div>
        <TopChart
          queryData={queryData}
          period={period}
          selectedYears={selectedYears}
          selectedMonths={selectedMonths}
          selectedTopItems={selectedTopItems}
          defaultStartMonth={defaultStartMonth}
          defaultMonth={defaultMonth}
          handleFilter={handleFilter}
        />

        <div id="wg_middle_chart">
          <div style={{ textAlign: 'center' }}>
            <strong
              style={{ fontFamily: 'Verdana !important', fontSize: '10pt' }}
            >
              Existing vs New
            </strong>
          </div>
          <Grid container spacing={1}>
            <Grid size={6}>
              <div style={{ width: '100%' }}>
                <MiddleChart
                  queryData={queryData}
                  selectedYears={selectedYears}
                  selectedMonths={selectedMonths}
                  selectedTopItems={selectedTopItems}
                  selectedMiddleItems={selectedMiddleItems}
                  filterName={filterName}
                  handleFilter={handleFilter}
                />
              </div>
            </Grid>
            <Grid size={6}>
              <div style={{ width: '100%' }}>
                <RightChart
                  queryData={queryData}
                  selectedYears={selectedYears}
                  selectedMonths={selectedMonths}
                  selectedTopItems={selectedTopItems}
                  selectedRightItems={selectedRightItems}
                  filterName={filterName}
                  mChartHeight={mChartHeight}
                  handleFilter={handleFilter}
                />
              </div>
            </Grid>
          </Grid>
        </div>

        <div
          style={{ height: '100%' }}
        >
          <BottomChart
            queryData={queryData}
            selectedYears={selectedYears}
            selectedMonths={selectedMonths}
            selectedTopItems={selectedTopItems}
            selectedMiddleItems={selectedMiddleItems}
            selectedRightItems={selectedRightItems}
            filterName={filterName}
          />
        </div>
      </div>

      {bubbleFlag && workGeneratedSummary && (
        <div
          className="reporting-tooltip"
          style={{
            position: 'fixed',
            bottom: 60,
            right: 30,
            zIndex: 1000,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '1'}
          onMouseLeave={(e) => e.target.style.opacity = '0.5'}
        >
          <Tooltip 
            title={workGeneratedSummary} 
            position="top-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#2c3e50',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }
              }
            }}
          >
            <InsertCommentIcon color="success" />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

WorkGenerated.propTypes = {
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  workGeneratedSetting: PropTypes.string.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  bubbleFlag: PropTypes.bool.isRequired,
};

export default WorkGenerated;
